import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { getApiErrorMessage } from '@/lib/utils/error.utils';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import {
  deleteInvestorProfile,
  saveInvestorProfile,
  type InvestorProfileData,
} from '../../api/investor-profile.api';

/** Matches `investorProfileModule.queryKey` (kept local to avoid a circular import back to `../investor`). */
const investorProfileQueryKey = (entityId: string) => ['profile', 'investor', entityId];

/** Saves the investor profile + preferences in one call (US-101 AC5) and refreshes the cache. */
export interface SaveInvestorProfileOptions {
  /** Runs after the cache is updated - the section panel closes and toasts here. */
  onSaved?: () => void;
}

export function useSaveInvestorProfile(entityId: string, options: SaveInvestorProfileOptions = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: InvestorProfileData) => saveInvestorProfile(entityId, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(investorProfileQueryKey(entityId), data);
      options.onSaved?.();
    },
  });
}

/**
 * Deletes the investor profile (US-040 AC8). The cached copy becomes the same shell the
 * backend returns for a profile that does not exist (FR-187: the firm name stays on the
 * header), then is refetched so the missing-fields notice is the server's own list.
 */
export function useDeleteInvestorProfile(entityId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteInvestorProfile(entityId),
    onSuccess: () => {
      const key = investorProfileQueryKey(entityId);
      const previous = queryClient.getQueryData<InvestorProfileData>(key);
      const shell: InvestorProfileData = {
        id: null,
        entityId,
        entityName: previous?.entityName,
        matchingReadinessPct: 0,
      };
      queryClient.setQueryData(key, shell);
      void queryClient.invalidateQueries({ queryKey: key });
      toast.success(UI_STRINGS.INVESTOR_PROFILE.DELETE_SUCCESS);
    },
    // The page behind it has no open surface left, so the API's reason goes to a toast.
    onError: (error) => {
      toast.error(getApiErrorMessage(error, UI_STRINGS.INVESTOR_PROFILE.DELETE_ERROR));
    },
  });
}
